import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type {
  Usuario,
  Rol,
  ParametrosGenerales,
  Personalizacion,
  TipoEPPConfig,
} from './models/configuracion.model';

const KEY_USUARIOS = 'configuracion_usuarios';
import { PERMISOS_DEF, TIPOS_EPP_INICIALES } from './models/configuracion.model';

/** Rol Administrador: tiene todos los permisos disponibles (todos los módulos). */
const ROLES_INICIALES: Rol[] = [
  {
    id: 'admin',
    nombre: 'Administrador',
    descripcion: 'Acceso total al sistema',
    permisos: [...PERMISOS_DEF.map((p) => p.clave)],
  },
  {
    id: 'sst',
    nombre: 'Responsable SST',
    descripcion: 'Gestión de SST sin administración de usuarios',
    permisos: PERMISOS_DEF.filter((p) => !p.clave.startsWith('configuracion_')).map((p) => p.clave),
  },
  {
    id: 'operador',
    nombre: 'Operador',
    descripcion: 'Solo consulta y registro en módulos asignados',
    permisos: [
      'dashboard_ver',
      'peligros_ver',
      'incidentes_ver',
      'incidentes_crear',
      'inspecciones_ver',
      'vigilancia_ver',
      'capacitacion_ver',
      'epp_ver',
      'epp_crear',
    ],
  },
  {
    id: 'visor',
    nombre: 'Solo lectura',
    descripcion: 'Solo consulta',
    permisos: [
      'dashboard_ver',
      'peligros_ver',
      'incidentes_ver',
      'inspecciones_ver',
      'vigilancia_ver',
      'capacitacion_ver',
      'epp_ver',
      'reportes_ver',
    ],
  },
];

function demoUsuarios(): Usuario[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const adminEmail = environment.demo ? 'admin@demo.com' : 'admin@empresa.com';
  return [
    { id: '1', nombre: 'Administrador', email: adminEmail, rolId: 'admin', activo: true, createdAt: now, updatedAt: now },
    { id: '2', nombre: 'Laura Méndez', email: 'laura.mendez@empresa.com', rolId: 'sst', activo: true, createdAt: now, updatedAt: now },
    { id: '3', nombre: 'Carlos Ruiz', email: 'carlos.ruiz@empresa.com', rolId: 'operador', activo: true, createdAt: now, updatedAt: now },
    { id: '4', nombre: 'Ana Consulta', email: 'ana@empresa.com', rolId: 'visor', activo: false, createdAt: now, updatedAt: now },
  ];
}

const PARAMETROS_DEFAULT: ParametrosGenerales = {
  nombreEmpresa: 'Mi Empresa SST',
  rut: '12.345.678-9',
  diasAlertaEpp: 30,
  diasAlertaVigilancia: 30,
  notificacionesEmail: true,
  notificacionesPush: false,
  recordatorioInspecciones: true,
  maxIntentosLogin: 5,
  bloqueoMinutos: 15,
};

const PERSONALIZACION_DEFAULT: Personalizacion = {
  tema: 'claro',
  idioma: 'es',
  formatoFecha: 'DD/MM/YYYY',
  zonaHoraria: 'America/Santiago',
  filasPorPagina: 10,
};

@Injectable({ providedIn: 'root' })
export class ConfiguracionService {
  private usuarios: Usuario[] = demoUsuarios();
  private roles: Rol[] = ROLES_INICIALES.map((r) => ({ ...r, permisos: [...r.permisos] }));
  private parametros: ParametrosGenerales = { ...PARAMETROS_DEFAULT };
  private personalizacion: Personalizacion = { ...PERSONALIZACION_DEFAULT };
  private tiposEpp: TipoEPPConfig[] = TIPOS_EPP_INICIALES.map((t) => ({ ...t }));

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const data = await this.demoStorage.get<Usuario[]>(KEY_USUARIOS);
    this.usuarios = Array.isArray(data) ? data : demoUsuarios();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return this.demoStorage.set(KEY_USUARIOS, this.usuarios).catch(() => {});
  }

  getUsuarios(filters?: { busqueda?: string; rolId?: string; activo?: boolean }): Usuario[] {
    let list = [...this.usuarios];
    if (filters?.busqueda?.trim()) {
      const q = filters.busqueda.trim().toLowerCase();
      list = list.filter(
        (u) =>
          u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    }
    if (filters?.rolId) list = list.filter((u) => u.rolId === filters.rolId);
    if (filters?.activo !== undefined) list = list.filter((u) => u.activo === filters.activo);
    return list.sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  getUsuarioById(id: string): Usuario | null {
    return this.usuarios.find((u) => u.id === id) ?? null;
  }

  getRoles(): Rol[] {
    return this.roles.map((r) => ({ ...r, permisos: [...r.permisos] }));
  }

  getRolById(id: string): Rol | null {
    const r = this.roles.find((r) => r.id === id);
    return r ? { ...r, permisos: [...r.permisos] } : null;
  }

  getParametros(): ParametrosGenerales {
    return { ...this.parametros };
  }

  getPersonalizacion(): Personalizacion {
    return { ...this.personalizacion };
  }

  createUsuario(dto: Omit<Usuario, 'id' | 'createdAt' | 'updatedAt'>): Usuario {
    const id = String(Date.now());
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: Usuario = { ...dto, id, createdAt: now, updatedAt: now };
    this.usuarios.push(nuevo);
    this.persistDemo();
    return nuevo;
  }

  updateUsuario(id: string, dto: Partial<Omit<Usuario, 'id' | 'createdAt'>>): Usuario | null {
    const idx = this.usuarios.findIndex((u) => u.id === id);
    if (idx === -1) return null;
    const updated: Usuario = {
      ...this.usuarios[idx],
      ...dto,
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
    };
    this.usuarios[idx] = updated;
    this.persistDemo();
    return updated;
  }

  deleteUsuario(id: string): boolean {
    const idx = this.usuarios.findIndex((u) => u.id === id);
    if (idx === -1) return false;
    this.usuarios.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  updateRol(id: string, permisos: string[]): Rol | null {
    const idx = this.roles.findIndex((r) => r.id === id);
    if (idx === -1) return null;
    this.roles[idx] = { ...this.roles[idx], permisos: [...permisos] };
    return this.roles[idx];
  }

  updateParametros(dto: Partial<ParametrosGenerales>): ParametrosGenerales {
    this.parametros = { ...this.parametros, ...dto };
    return this.getParametros();
  }

  updatePersonalizacion(dto: Partial<Personalizacion>): Personalizacion {
    this.personalizacion = { ...this.personalizacion, ...dto };
    return this.getPersonalizacion();
  }

  getTiposEpp(): TipoEPPConfig[] {
    return this.tiposEpp.map((t) => ({ ...t }));
  }

  createTipoEpp(codigo: string, nombre: string): TipoEPPConfig | null {
    const c = codigo.trim().toLowerCase().replace(/\s+/g, '_');
    if (!c || this.tiposEpp.some((t) => t.codigo === c)) return null;
    const nuevo = { codigo: c, nombre: nombre.trim() || c };
    this.tiposEpp.push(nuevo);
    return { ...nuevo };
  }

  updateTipoEpp(codigoActual: string, codigo: string, nombre: string): TipoEPPConfig | null {
    const idx = this.tiposEpp.findIndex((t) => t.codigo === codigoActual);
    if (idx === -1) return null;
    const c = codigo.trim().toLowerCase().replace(/\s+/g, '_');
    if (!c) return null;
    if (c !== codigoActual && this.tiposEpp.some((t) => t.codigo === c)) return null;
    this.tiposEpp[idx] = { codigo: c, nombre: nombre.trim() || c };
    return { ...this.tiposEpp[idx] };
  }

  deleteTipoEpp(codigo: string): boolean {
    const idx = this.tiposEpp.findIndex((t) => t.codigo === codigo);
    if (idx === -1) return false;
    this.tiposEpp.splice(idx, 1);
    return true;
  }
}
